/**
 * Customer Reviews Section
 * Fetches and displays customer reviews from Supabase API using Swiper slider
 */

class CustomerReviews {
  constructor(container) {
    this.reviewsContainer = container;
    this.apiUrl = 'https://hjdomxzbruxedccddyna.supabase.co/rest/v1/reviews?select=*&order=date.desc&limit=10';
    this.headers = {
      Authorization:
        'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhqZG9teHpicnV4ZWRjY2RkeW5hIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM3MjcxMzEsImV4cCI6MjA2OTMwMzEzMX0.6sMWnC7DOZ_e7jrmF1KVTSIreCz5JkCmYEP12RRRK-w',
      apikey:
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhqZG9teHpicnV4ZWRjY2RkeW5hIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM3MjcxMzEsImV4cCI6MjA2OTMwMzEzMX0.6sMWnC7DOZ_e7jrmF1KVTSIreCz5JkCmYEP12RRRK-w',
    };

    this.init();
  }

  init() {
    if (!this.reviewsContainer) return;

    this.showLoading();

    // Use requestIdleCallback for non-blocking initialization
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          this.fetchReviews();
        },
        { timeout: 2000 }
      );
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        this.fetchReviews();
      }, 100);
    }
  }

  showLoading() {
    this.reviewsContainer.innerHTML = '<div class="loading-spinner">Loading customer reviews...</div>';
  }

  async fetchReviews() {
    try {
      // Use AbortController for better control
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(this.apiUrl, {
        headers: this.headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reviews = await response.json();

      // Use requestAnimationFrame for smooth DOM updates
      requestAnimationFrame(() => {
        if (reviews && reviews.length > 0) {
          this.renderReviews(reviews);

          // Use requestIdleCallback for Swiper initialization
          if ('requestIdleCallback' in window) {
            requestIdleCallback(
              () => {
                this.initializeSwiper();
                this.addLoadedClass();
              },
              { timeout: 1000 }
            );
          } else {
            setTimeout(() => {
              this.initializeSwiper();
              this.addLoadedClass();
            }, 50);
          }
        } else {
          this.showNoReviews();
        }
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
      requestAnimationFrame(() => {
        this.showError();
      });
    }
  }

  renderReviews(reviews) {
    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();

    reviews.forEach((review) => {
      const initials = this.getInitials(review.name);
      const formattedDate = this.formatDate(review.date);
      const stars = this.generateStars(review.rating);

      // Adaptive truncation based on screen size
      const isMobile = window.innerWidth <= 768;
      const isSmallMobile = window.innerWidth <= 480;
      const maxLength = isSmallMobile ? 200 : isMobile ? 300 : 330;
      const truncatedMessage = this.truncateText(review.message, maxLength);

      const slideDiv = document.createElement('div');
      slideDiv.className = 'swiper-slide';
      slideDiv.innerHTML = `
        <a href="${review.link}" target="_blank" rel="noopener noreferrer" class="review-card-link">
          <div class="review-card">
            <div class="review-header">
              <div class="review-profile">
                ${
                  review.profileImage
                    ? `<img src="${review.profileImage}" alt="${review.name}">`
                    : `<div class="review-profile-initials">${initials}</div>`
                }
              </div>
              <div class="review-info">
                <h3 class="review-name">${review.name}</h3>
                <p class="review-date">${formattedDate}</p>
                                  <div class="review-rating">
                    ${stars}
                  </div>
              </div>
            </div>
            <p class="review-message">${truncatedMessage}</p>
          </div>
        </a>
      `;

      fragment.appendChild(slideDiv);
    });

    // Clear container and append fragment
    this.reviewsContainer.innerHTML = '';
    this.reviewsContainer.appendChild(fragment);
  }

  getInitials(name) {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase();
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return '1 day ago';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
  }

  generateStars(rating) {
    const fullStars = '<span class="star-slider">★</span>';
    const emptyStars = '<span class="star-slider" style="color: #ccc;">☆</span>';
    return fullStars.repeat(rating) + emptyStars.repeat(5 - rating);
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) {
      return text;
    }

    // Find the last space before maxLength to avoid cutting words
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    // If we can't find a space, just truncate at maxLength
    if (lastSpace <= 0) {
      return truncated + '...';
    }

    // Try to find a better break point (sentence end)
    const lastSentenceEnd = truncated.lastIndexOf('.');
    const lastExclamation = truncated.lastIndexOf('!');
    const lastQuestion = truncated.lastIndexOf('?');

    const bestBreak = Math.max(lastSentenceEnd, lastExclamation, lastQuestion);

    if (bestBreak > lastSpace * 0.7 && bestBreak > 0) {
      // If sentence end is not too far from space
      return truncated.substring(0, bestBreak + 1) + '...';
    }

    // Always add ellipsis when truncating
    return truncated.substring(0, lastSpace) + '...';
  }

  initializeSwiper() {
    if (typeof Swiper === 'undefined') {
      console.error('Swiper is not loaded');
      return;
    }

    // Find the parent swiper container for this specific reviews container
    const swiperContainer = this.reviewsContainer.closest('.customer-reviews-swiper');
    if (!swiperContainer) return;

    new Swiper(swiperContainer, {
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      pagination: {
        el: swiperContainer.querySelector('.swiper-pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: swiperContainer.querySelector('.swiper-button-next'),
        prevEl: swiperContainer.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 30,
        },
      },
    });
  }

  showNoReviews() {
    this.reviewsContainer.innerHTML = '<div class="error-message">No reviews available at the moment</div>';
  }

  showError() {
    this.reviewsContainer.innerHTML =
      '<div class="error-message">Unable to load reviews. Please try again later.</div>';
  }

  addLoadedClass() {
    const swiperElement = this.reviewsContainer.closest('.customer-reviews-swiper');
    if (swiperElement) {
      swiperElement.classList.add('loaded');
    }
  }
}

// Initialize when DOM is loaded with non-blocking approach
document.addEventListener('DOMContentLoaded', function () {
  // Use requestIdleCallback for non-blocking initialization
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        initializeAllReviewsSections();
      },
      { timeout: 3000 }
    );
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      initializeAllReviewsSections();
    }, 200);
  }
});

// Initialize all reviews sections on the page
function initializeAllReviewsSections() {
  const reviewsContainers = document.querySelectorAll('[data-reviews-section]');

  reviewsContainers.forEach((container) => {
    new CustomerReviews(container);
  });
}
